/**
 * Saving an API response to disk.
 *
 * Export endpoints answer with a spreadsheet rather than JSON, which means the
 * request runs with `responseType: "blob"`. That has two consequences worth
 * handling in one place: the filename lives in a header rather than the body,
 * and a *failed* request arrives as a blob too — so the server's error message
 * has to be read out of it instead of off `response.data`.
 */

/** MIME type -> file extension, for when the server doesn't name the file. */
const EXTENSION_BY_TYPE = {
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-excel": "xls",
  "text/csv": "csv",
  "application/json": "json"
}

/** ".xlsx" is the default: these exports are spreadsheets unless stated. */
export const extensionForType = (type) => {
  const base = String(type ?? "").split(";")[0].trim().toLowerCase()
  return EXTENSION_BY_TYPE[base] ?? "xlsx"
}

/**
 * The server's own filename, when it sent one.
 *
 * Returns null far more often than you'd expect: `Content-Disposition` is not a
 * CORS-safelisted response header, so unless the API adds it to
 * `Access-Control-Expose-Headers` the browser hides it from JavaScript. Callers
 * need a fallback name.
 */
export const filenameFromDisposition = (disposition) => {
  if (!disposition) return null

  // RFC 5987 (`filename*=UTF-8''report%202026.xlsx`) wins where present — it's
  // the form that survives non-ASCII characters.
  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition)
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ""))
    } catch {
      // Malformed percent-encoding — fall through to the plain form.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(disposition)
  return plain?.[1]?.trim() || null
}

/** Hand a blob to the browser as a download. */
export const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Revoking synchronously can cancel the download in some browsers; letting
  // the current task finish first is enough.
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0)
}

/**
 * Save an axios blob response, preferring the server's filename.
 *
 * @param {import('axios').AxiosResponse} response a `responseType: "blob"` response
 * @param {string} fallbackBase filename without extension, used when the
 *   server didn't expose one
 */
export const saveResponseAsFile = (response, fallbackBase) => {
  const blob = response?.data
  if (!(blob instanceof Blob)) throw new Error("Response body is not a file.")

  const disposition =
    response?.headers?.["content-disposition"] ?? response?.headers?.get?.("content-disposition")

  const filename =
    filenameFromDisposition(disposition) || `${fallbackBase}.${extensionForType(blob.type)}`

  saveBlob(blob, filename)
  return filename
}

/**
 * A blob's contents as text.
 *
 * `Blob.text()` where the environment has it, `FileReader` where it doesn't —
 * jsdom is the second kind, which is why every test that went through a blob
 * error body used to see `null` regardless of what the server had said.
 */
export const blobText = (blob) => {
  if (typeof blob?.text === "function") return blob.text()

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ""))
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the response."))
    reader.readAsText(blob)
  })
}

/**
 * The server's error message from a failed blob request.
 *
 * `error.response.data` is a Blob here, not the parsed JSON the rest of the app
 * expects, so reading `.error_message` off it silently yields undefined.
 */
export const readBlobError = async (error) => {
  const data = error?.response?.data
  if (!(data instanceof Blob)) return data?.error_message ?? null

  try {
    const parsed = JSON.parse(await blobText(data))
    return parsed?.error_message || parsed?.detail || null
  } catch {
    return null
  }
}

// -----------------------------------------------------------------------------
// Exports that answer with a link instead of the file
// -----------------------------------------------------------------------------

/**
 * The URL an export named, wherever the envelope put it.
 *
 * Some export endpoints stream the spreadsheet back; others write it to S3 and
 * answer `{ data: { file_url } }` with a presigned link. The two need opposite
 * handling — one is a blob request, the other an ordinary JSON one — so a
 * caller has to know which kind it is calling. This reads the second kind.
 */
export const fileUrlFrom = (response) =>
  response?.data?.data?.file_url ?? response?.data?.file_url ?? null

/**
 * Follow a download link the API handed back.
 *
 * Deliberately not `download="name.xlsx"`: browsers ignore that attribute on a
 * cross-origin href, so setting it would suggest a filename this code cannot
 * actually impose. The link is opened and the server's own
 * `Content-Disposition` decides what the file is called.
 *
 * `noopener` because the href is a third-party URL — without it the opened tab
 * gets a handle on `window.opener`.
 */
export const openFileUrl = (url) => {
  if (!url) throw new Error("The export did not return a file.")

  const link = document.createElement("a")
  link.href = url
  link.target = "_blank"
  link.rel = "noopener noreferrer"
  document.body.appendChild(link)
  link.click()
  link.remove()

  return url
}
